import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Modal, ModalVariant, toast } from '@/components';
import { ReportEventFrameSelectStep } from '@/components/ReportEventEditor';
import type { ReportEventFrameSelectStepHandle } from '@/components/ReportEventEditor';
import { getSourceCacheKey } from '@/constants/cache-keys';
import { useAddEventsToReport } from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { ReportEventDraft, ReportListItem } from '@/types/report';
import { eventVideoStartSeconds } from '@/utils/eventVideoStartSeconds';
import { appendThumbnailFrame } from '@/utils/report-event-thumbnails';
import { AddEventsToReportContent } from './components/AddEventsToReportContent';
import { useReportEventDrafts } from './hooks/useReportEventDrafts';

interface AddEventsToReportModalProps {
    report: ReportListItem;
    initialDrafts: ReportEventDraft[];
    onClose: () => void;
    onAdded: () => void;
}

type Step = { kind: 'list' } | { kind: 'addThumbnail'; eventId: string };

export function AddEventsToReportModal({
    report,
    initialDrafts,
    onClose,
    onAdded,
}: AddEventsToReportModalProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { busy, addEventsToReport } = useAddEventsToReport();
    const { drafts, selectedEventId, selectEvent, updateById, removeById } =
        useReportEventDrafts(initialDrafts);
    const [step, setStep] = useState<Step>({ kind: 'list' });
    const [validationVisible, setValidationVisible] = useState(false);
    const [managePopoverOpen, setManagePopoverOpen] = useState(false);
    const [frameSelectSubmitDisabled, setFrameSelectSubmitDisabled] =
        useState(true);
    const frameSelectStepRef = useRef<ReportEventFrameSelectStepHandle | null>(
        null,
    );

    const selectedDraft =
        drafts.find((draft) => draft.event.id === selectedEventId) ?? drafts[0];
    const frameSelectDraft =
        step.kind === 'addThumbnail'
            ? drafts.find((draft) => draft.event.id === step.eventId)
            : undefined;
    const frameSelectSourceId = frameSelectDraft?.event.sourceId;

    const {
        data: frameSelectSource,
        isLoading: frameSelectSourceLoading,
        error: frameSelectSourceError,
    } = useSWR(
        frameSelectSourceId ? getSourceCacheKey(frameSelectSourceId) : null,
        () => EngineService.getSource(frameSelectSourceId as string),
    );

    useEffect(() => {
        if (!frameSelectSourceError) {
            return;
        }
        toast.error(t('addToReport.modal.sourceLoadError'));
        setStep({ kind: 'list' });
    }, [frameSelectSourceError, t]);

    if (!selectedDraft) {
        return null;
    }

    const handleSubmit = async () => {
        const firstInvalid = drafts.find((draft) => !draft.promptText.trim());
        if (firstInvalid) {
            selectEvent(firstInvalid.event.id);
            setValidationVisible(true);
            return;
        }
        const result = await addEventsToReport({ report, drafts });
        if (result.added > 0) {
            onAdded();
        }
    };

    const handleFrameConfirmed = (payload: {
        time: number;
        bbox: [number, number, number, number];
    }) => {
        if (step.kind !== 'addThumbnail' || !frameSelectDraft) {
            return;
        }
        updateById(step.eventId, {
            thumbnails: appendThumbnailFrame(
                frameSelectDraft.thumbnails,
                payload,
            ),
        });
        selectEvent(step.eventId);
        setStep({ kind: 'list' });
    };

    const handleStartAddThumbnail = () => {
        setManagePopoverOpen(false);
        setFrameSelectSubmitDisabled(true);
        setStep({ kind: 'addThumbnail', eventId: selectedDraft.event.id });
    };

    const handleBackToList = () => setStep({ kind: 'list' });

    const showFrameSelect = step.kind === 'addThumbnail';
    const frameSelectReady =
        Boolean(frameSelectSource) && !frameSelectSourceLoading;

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={
                showFrameSelect
                    ? t('addToReport.modal.addThumbnailTitle')
                    : t('addToReport.modal.title')
            }
            onCancel={showFrameSelect ? handleBackToList : onClose}
            onSubmit={
                showFrameSelect
                    ? () => frameSelectStepRef.current?.confirm()
                    : () => void handleSubmit()
            }
            submitText={
                showFrameSelect
                    ? t('addToReport.modal.confirmAddThumbnail')
                    : t('addToReport.modal.submit', { count: drafts.length })
            }
            submitDisabled={
                showFrameSelect
                    ? frameSelectSubmitDisabled || !frameSelectReady
                    : busy
            }
            submitInProgress={busy}
            variant={ModalVariant.DEFAULT}
            maxWidth="max-w-[860px]"
            maxHeight="h-[658px] max-h-[calc(100dvh-4rem)]"
            onBack={showFrameSelect ? handleBackToList : undefined}
            closeOnOutsideClick
        >
            {showFrameSelect ? (
                frameSelectReady && frameSelectSource && frameSelectDraft ? (
                    <ReportEventFrameSelectStep
                        ref={frameSelectStepRef}
                        source={frameSelectSource}
                        eventStartSeconds={eventVideoStartSeconds(
                            frameSelectDraft.event,
                        )}
                        onConfirm={handleFrameConfirmed}
                        onSubmitDisabledChange={setFrameSelectSubmitDisabled}
                    />
                ) : (
                    <div className="p-10 text-center text-grey-300">
                        {t('addToReport.modal.loadingSource')}
                    </div>
                )
            ) : (
                <AddEventsToReportContent
                    drafts={drafts}
                    selectedDraft={selectedDraft}
                    validationVisible={validationVisible}
                    managePopoverOpen={managePopoverOpen}
                    onManagePopoverOpenChange={setManagePopoverOpen}
                    onSelect={selectEvent}
                    onRemove={removeById}
                    onEditSelected={(edits) =>
                        updateById(selectedDraft.event.id, edits)
                    }
                    onStartAddThumbnail={handleStartAddThumbnail}
                    promptTextError={
                        validationVisible && !selectedDraft.promptText.trim()
                            ? t('addToReport.modal.nameRequired')
                            : undefined
                    }
                />
            )}
        </Modal>
    );
}
